import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface GammaMarket {
  conditionId: string;
  question: string;
  slug: string;
  clobTokenIds: string; // JSON array
  outcomes: string; // JSON array
}

interface DataApiTrade {
  proxyWallet: string;
  side: "BUY" | "SELL";
  asset: string;
  conditionId: string;
  size: number;
  price: number;
  timestamp: number;
  title: string;
  slug: string;
  eventSlug: string;
  outcome: string;
  outcomeIndex: number;
  transactionHash?: string;
  name?: string;
  pseudonym?: string;
}

/** Resolve a market ID to its metadata via our Next.js proxy */
async function resolveMarket(
  marketId: string,
): Promise<{ conditionId: string; question: string; slug: string; tokenIds: string[]; outcomes: string[] } | null> {
  // If it looks like a condition ID, look it up by condition_id
  if (marketId.startsWith("0x")) {
    const res = await fetch(`/api/polymarket/markets?conditionId=${encodeURIComponent(marketId)}`, {
      signal: AbortSignal.timeout(10_000),
    });
    if (!res.ok) return null;
    const markets = (await res.json()) as GammaMarket[];
    // Gamma API can return unrelated markets — filter to exact match
    const idLower = marketId.toLowerCase();
    const m = markets.find(m => m.conditionId.toLowerCase() === idLower);
    if (!m) return null;
    return {
      conditionId: m.conditionId,
      question: m.question,
      slug: m.slug,
      tokenIds: JSON.parse(m.clobTokenIds) as string[],
      outcomes: JSON.parse(m.outcomes) as string[],
    };
  }

  // Otherwise treat it as a slug — use the event proxy
  const res = await fetch(`/api/polymarket/events/${encodeURIComponent(marketId)}`, {
    signal: AbortSignal.timeout(10_000),
  });
  if (res.ok) {
    const event = (await res.json()) as { markets?: GammaMarket[] };
    if (event.markets?.length) {
      const m = event.markets[0];
      return {
        conditionId: m.conditionId,
        question: m.question,
        slug: m.slug,
        tokenIds: JSON.parse(m.clobTokenIds) as string[],
        outcomes: JSON.parse(m.outcomes) as string[],
      };
    }
  }

  return null;
}

/**
 * Fetch recent trades for a Polymarket market via our Data API trades proxy.
 * Falls back to empty if the market cannot be resolved.
 */
export const polymarketMarketTradeFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { triggerPayload } = ctx;
  const marketId = triggerPayload.marketId as string | undefined;
  if (!marketId) return [];

  try {
    const market = await resolveMarket(marketId);
    if (!market) return [];

    // The Data API `market` param accepts a conditionId, filtering trades
    // server-side to this specific market (avoids neg-risk token sharing).
    const res = await fetch(`/api/polymarket/trades?market=${encodeURIComponent(market.conditionId)}&limit=5`, {
      signal: AbortSignal.timeout(10_000),
    });
    if (!res.ok) return [];
    const trades = (await res.json()) as DataApiTrade[];
    if (!Array.isArray(trades) || !trades.length) return [];

    return trades.map(t => {
      const timestamp = new Date(t.timestamp * 1000).toISOString();

      return {
        triggeredAt: timestamp,
        market: {
          conditionId: t.conditionId || market.conditionId,
          question: t.title || market.question,
          slug: t.slug || market.slug,
          tokenId: t.asset,
          outcome: t.outcome,
        },
        trade: {
          side: t.side,
          price: t.price,
          size: t.size,
          amount: Math.round(t.price * t.size * 100) / 100,
          timestamp,
          transactionHash: t.transactionHash ?? null,
          trader: t.proxyWallet,
        },
        _question: t.title || market.question,
        _outcome: t.outcome,
        _side: t.side,
        _size: t.size,
        _price: t.price,
        _txHash: t.transactionHash ?? null,
      };
    });
  } catch {
    return [];
  }
};
